/**
 * Ink Document Service
 * High-level service for managing Ink App documents with database persistence
 * Handles: load, save, auto-save, versioning, and block management
 */
import { inkService } from './ink.service';
import { supabase } from '../lib/supabase-client';
/**
 * Load document from ink table
 * Returns document state ready for editing
 */
export async function loadInkDocument(documentId) {
    try {
        const doc = await inkService.getById(documentId);
        if (!doc) {
            console.error(`Document ${documentId} not found`);
            return null;
        }
        // Increment view count asynchronously (don't await)
        inkService.incrementViews(documentId).catch(err => console.error('Error incrementing views:', err));
        // Convert stored blocks to component blocks
        const blocks = (doc.blocks || []).map((block) => ({
            id: block.id,
            type: block.type,
            content: block.content || '',
            placeholder: block.placeholder,
            tableData: block.data?.tableData,
            signatureData: block.data?.signatureData,
            formFieldData: block.data?.formFieldData
        }));
        return {
            id: doc.id,
            title: doc.title || 'Untitled Document',
            blocks: blocks.length > 0 ? blocks : [{
                    id: '1',
                    type: 'paragraph',
                    content: '',
                    placeholder: 'Type / for commands...'
                }],
            documentMode: (doc.status === 'published' ? 'view' : 'edit'),
            isDirty: false,
            isSaving: false
        };
    }
    catch (error) {
        console.error('Error loading ink document:', error);
        return null;
    }
}
/**
 * Create new document in ink table
 */
export async function createInkDocument(organizationId, title, workspaceId) {
    try {
        const user = (await supabase.auth.getUser()).data.user;
        if (!user) {
            console.error('User not authenticated');
            return null;
        }
        const newDoc = await inkService.createInkDocument(organizationId, title, workspaceId);
        if (!newDoc)
            return null;
        // Log activity
        await inkService.logActivity(newDoc.id, 'created');
        return {
            id: newDoc.id,
            title: newDoc.title,
            blocks: [{
                    id: '1',
                    type: 'paragraph',
                    content: '',
                    placeholder: 'Type / for commands...'
                }],
            documentMode: 'edit',
            isDirty: false,
            isSaving: false
        };
    }
    catch (error) {
        console.error('Error creating ink document:', error);
        return null;
    }
}
/**
 * Save document blocks to ink table
 * Converts component blocks to storage format
 */
export async function saveInkDocument(documentId, title, blocks) {
    try {
        // Convert component blocks to storage format
        const storageBlocks = blocks.map(block => ({
            id: block.id,
            type: block.type,
            content: block.content || '',
            data: {
                tableData: block.tableData,
                signatureData: block.signatureData,
                formFieldData: block.formFieldData
            },
            createdAt: new Date().toISOString()
        }));
        // Update blocks order
        const blocksOrder = blocks.map(b => b.id);
        // Calculate word count
        const wordCount = blocks.reduce((total, block) => {
            return total + (block.content?.split(/\s+/).length || 0);
        }, 0);
        // Detect signatures and forms
        const hasSig = blocks.some(b => b.type === 'signature' && b.signatureData);
        const hasForms = blocks.some(b => b.type === 'form-field');
        // Save to database
        const updated = await inkService.update(documentId, {
            title,
            blocks: storageBlocks,
            blocks_order: blocksOrder,
            word_count: wordCount,
            block_count: blocks.length,
            has_signatures: hasSig,
            has_forms: hasForms,
            edited_at: new Date().toISOString()
        });
        if (updated) {
            // Log activity
            await inkService.logActivity(documentId, 'edited', undefined, {
                blockCount: blocks.length,
                wordCount
            });
        }
        return updated;
    }
    catch (error) {
        console.error('Error saving ink document:', error);
        return null;
    }
}
/**
 * Delete document (soft delete)
 */
export async function deleteInkDocument(documentId) {
    try {
        const success = await inkService.delete(documentId);
        if (success) {
            await inkService.logActivity(documentId, 'deleted');
        }
        return success;
    }
    catch (error) {
        console.error('Error deleting ink document:', error);
        return false;
    }
}
/**
 * Publish document
 */
export async function publishInkDocument(documentId) {
    try {
        const published = await inkService.publish(documentId);
        if (published) {
            await inkService.logActivity(documentId, 'published');
        }
        return published;
    }
    catch (error) {
        console.error('Error publishing ink document:', error);
        return null;
    }
}
/**
 * Share document with collaborator
 */
export async function shareInkDocument(documentId, userId, role = 'editor') {
    try {
        const shared = await inkService.addCollaborator(documentId, userId, role);
        if (shared) {
            await inkService.logActivity(documentId, 'shared', undefined, {
                collaborator: userId,
                role
            });
        }
        return shared;
    }
    catch (error) {
        console.error('Error sharing ink document:', error);
        return null;
    }
}
/**
 * Request signature for a block
 */
export async function requestSignature(documentId, blockId, assigneeEmail, assigneeName) {
    try {
        const doc = await inkService.getById(documentId);
        if (!doc)
            return null;
        const signatureRequests = doc.signature_requests || [];
        // Add new signature request if not already exists
        if (!signatureRequests.find((req) => req.blockId === blockId)) {
            signatureRequests.push({
                blockId,
                assignee: assigneeEmail,
                assigneeName: assigneeName || assigneeEmail,
                status: 'pending',
                requestedAt: new Date().toISOString()
            });
        }
        const updated = await inkService.update(documentId, {
            signature_requests: signatureRequests,
            has_signatures: true
        });
        if (updated) {
            await inkService.logActivity(documentId, 'signature_requested', blockId, {
                assignee: assigneeEmail
            });
        }
        return updated;
    }
    catch (error) {
        console.error('Error requesting signature:', error);
        return null;
    }
}
/**
 * Add comment to block
 */
export async function addBlockComment(documentId, blockId, content) {
    try {
        const user = (await supabase.auth.getUser()).data.user;
        if (!user)
            return null;
        const { data, error } = await supabase
            .from('ink_comments')
            .insert([{
                document_id: documentId,
                block_id: blockId,
                author_id: user.id,
                content,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            }])
            .select()
            .single();
        if (error)
            throw error;
        // Log activity
        await inkService.logActivity(documentId, 'commented', blockId, {
            comment: content
        });
        return data;
    }
    catch (error) {
        console.error('Error adding block comment:', error);
        return null;
    }
}
/**
 * Get block comments
 */
export async function getBlockComments(documentId, blockId) {
    try {
        const { data, error } = await supabase
            .from('ink_comments')
            .select('*')
            .eq('document_id', documentId)
            .eq('block_id', blockId)
            .eq('resolved', false)
            .order('created_at', { ascending: false });
        if (error)
            throw error;
        return data || [];
    }
    catch (error) {
        console.error('Error fetching block comments:', error);
        return [];
    }
}
/**
 * Get activity log for document
 */
export async function getDocumentActivity(documentId, limit = 20) {
    try {
        const { data, error } = await supabase
            .from('ink_activity')
            .select('*')
            .eq('document_id', documentId)
            .order('created_at', { ascending: false })
            .limit(limit);
        if (error)
            throw error;
        return data || [];
    }
    catch (error) {
        console.error('Error fetching activity log:', error);
        return [];
    }
}
